import useFetch from "hooks/useFetch";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Skeleton,
  styles as ui,
  TextField,
  Toggle,
  useToast,
} from "stories/Admin";
import PageModal from "../LocationsPage/PageModal";
import { FuneralAgency, keyFromName, move } from "./janazah";
import styles from "./FuneralAgenciesPage.module.scss";

/*
 * Superadmin: begravelsesbyråene appene viser på janazah-siden, og som AI-en kjenner igjen på
 * opplastede plakater (navn og aliaser). Byråer med Memcare-id hentes også automatisk av synken.
 */

const BASE = "/api/v1/funeral-agencies/admin";

interface Form {
  id: number | null;
  key: string;
  name: string;
  website: string;
  phone: string;
  email: string;
  memorialListUrl: string;
  memcareFuneralHome: string;
  aliases: string[];
  active: boolean;
}

const emptyForm: Form = {
  id: null,
  key: "",
  name: "",
  website: "",
  phone: "",
  email: "",
  memorialListUrl: "",
  memcareFuneralHome: "",
  aliases: [],
  active: true,
};

const toForm = (a: FuneralAgency): Form => ({
  id: a.id,
  key: a.key,
  name: a.name,
  website: a.website ?? "",
  phone: a.phone ?? "",
  email: a.email ?? "",
  memorialListUrl: a.memorialListUrl ?? "",
  memcareFuneralHome: a.memcareFuneralHome ?? "",
  aliases: a.aliases ?? [],
  active: a.active,
});

const initial = (name: string) =>
  name
    .replace(/^al[- ]/i, "")
    .trim()
    .charAt(0)
    .toUpperCase() || "?";

const Logo: React.FC<{
  agency: Pick<FuneralAgency, "name" | "logoUrl">;
  size?: number;
}> = ({ agency, size = 44 }) =>
  agency.logoUrl ? (
    <img
      src={agency.logoUrl}
      alt={`Logoen til ${agency.name}`}
      className={styles.logo}
      style={{ width: size, height: size }}
    />
  ) : (
    <span
      className={styles.logoFallback}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {initial(agency.name)}
    </span>
  );

const FuneralAgenciesPage: React.FC = () => {
  const { execute, lastError } = useFetch();
  const executeRef = useRef(execute);
  executeRef.current = execute;
  const toast = useToast();

  const [agencies, setAgencies] = useState<FuneralAgency[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState<Form | null>(null);
  const [aliasDraft, setAliasDraft] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    const list = (await executeRef.current(BASE, "GET")) as
      | FuneralAgency[]
      | null;
    if (Array.isArray(list)) setAgencies(list);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const pickLogo = (f: File | null) => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(f);
    setLogoPreview(f ? URL.createObjectURL(f) : null);
  };

  const openForm = (a?: FuneralAgency) => {
    setForm(a ? toForm(a) : { ...emptyForm });
    setAliasDraft("");
    pickLogo(null);
  };

  const closeForm = () => {
    setForm(null);
    pickLogo(null);
  };

  const addAlias = () => {
    if (!form) return;
    const value = aliasDraft.trim();
    if (!value) return;
    if (!form.aliases.some((a) => a.toLowerCase() === value.toLowerCase())) {
      setForm({ ...form, aliases: [...form.aliases, value] });
    }
    setAliasDraft("");
  };

  const save = async () => {
    if (!form) return;
    const name = form.name.trim();
    const key = (form.key || keyFromName(name)).trim();
    if (!name || !key) {
      toast.error("Navn og nøkkel må fylles ut.");
      return;
    }
    // Et alias som står i feltet uten å være lagt til, tas med.
    const aliases =
      aliasDraft.trim() && !form.aliases.includes(aliasDraft.trim())
        ? [...form.aliases, aliasDraft.trim()]
        : form.aliases;
    setIsSaving(true);
    const body = {
      key,
      name,
      website: form.website.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      memorialListUrl: form.memorialListUrl.trim(),
      memcareFuneralHome: form.memcareFuneralHome.trim(),
      aliases,
      active: form.active,
    };
    let saved = (await executeRef.current(
      form.id ? `${BASE}/${form.id}` : BASE,
      form.id ? "PUT" : "POST",
      body,
    )) as FuneralAgency | null;
    if (!saved) {
      setIsSaving(false);
      toast.error(lastError()?.message ?? "Kunne ikke lagre byrået.");
      return;
    }
    if (logoFile) {
      const data = new FormData();
      data.append("file", logoFile);
      const withLogo = (await executeRef.current(
        `${BASE}/${saved.id}/logo`,
        "POST",
        data,
      )) as FuneralAgency | null;
      if (withLogo) saved = withLogo;
      else
        toast.error(
          lastError()?.message ??
            "Byrået er lagret, men logoen kunne ikke lastes opp.",
        );
    }
    setIsSaving(false);
    toast.success(`${saved.name} er lagret.`);
    closeForm();
    load();
  };

  const setActive = async (a: FuneralAgency, active: boolean) => {
    setBusyId(a.id);
    const saved = (await executeRef.current(`${BASE}/${a.id}`, "PUT", {
      active,
    })) as FuneralAgency | null;
    setBusyId(null);
    if (!saved) {
      toast.error(lastError()?.message ?? "Kunne ikke endre byrået.");
      return;
    }
    setAgencies(
      (prev) => prev?.map((x) => (x.id === saved.id ? saved : x)) ?? prev,
    );
    toast.success(
      active ? `${a.name} vises i appen.` : `${a.name} er skjult i appen.`,
    );
  };

  const reorder = async (index: number, delta: -1 | 1) => {
    if (!agencies) return;
    const next = move(agencies, index, delta);
    if (next === agencies) return;
    setAgencies(next);
    const saved = (await executeRef.current(`${BASE}/order`, "PUT", {
      ids: next.map((a) => a.id),
    })) as FuneralAgency[] | null;
    if (Array.isArray(saved)) setAgencies(saved);
    else {
      toast.error("Kunne ikke lagre rekkefølgen.");
      load();
    }
  };

  return (
    <>
      <PageHeader
        title="Begravelsesbyråer"
        subtitle="Byråene i appenes janazah-side. AI-en bruker navn og aliaser for å kjenne igjen byrået på opplastede plakater."
        actions={
          <>
            <Button
              variant="secondary"
              icon={<Icon name="swap" size={16} />}
              onClick={load}
              loading={isLoading}
            >
              Oppdater
            </Button>
            <Button
              icon={<Icon name="plus" size={16} strokeWidth={2} />}
              onClick={() => openForm()}
            >
              Nytt byrå
            </Button>
          </>
        }
      />
      <SectionTabs />

      <Card>
        {isLoading && !agencies ? (
          [0, 1, 2].map((i) => <Skeleton key={i} height={64} radius={14} />)
        ) : !agencies ? (
          <Alert tone="error">
            Kunne ikke hente byråene. Prøv å oppdatere.
          </Alert>
        ) : agencies.length === 0 ? (
          <EmptyState icon="moon" title="Ingen byråer ennå">
            Trykk «Nytt byrå» for å legge til et begravelsesbyrå.
          </EmptyState>
        ) : (
          <ul className={styles.list}>
            {agencies.map((a, index) => (
              <li
                key={a.id}
                className={`${styles.item} ${a.active ? "" : styles.inactive}`}
              >
                <div className={styles.order}>
                  <IconButton
                    size="sm"
                    label={`Flytt ${a.name} opp`}
                    disabled={index === 0}
                    onClick={() => reorder(index, -1)}
                    icon={
                      <Icon
                        name="chevronLeft"
                        size={16}
                        strokeWidth={2}
                        className={styles.up}
                      />
                    }
                  />
                  <IconButton
                    size="sm"
                    label={`Flytt ${a.name} ned`}
                    disabled={index === agencies.length - 1}
                    onClick={() => reorder(index, 1)}
                    icon={
                      <Icon
                        name="chevronRight"
                        size={16}
                        strokeWidth={2}
                        className={styles.down}
                      />
                    }
                  />
                </div>
                <Logo agency={a} />
                <div className={styles.info}>
                  <span className={styles.nameLine}>
                    <b>{a.name}</b>
                    <Badge tone="grey">{a.key}</Badge>
                    {!a.active && <Badge tone="gold">Skjult</Badge>}
                    {a.memcareFuneralHome && (
                      <Badge tone="blue">Synkes fra Memcare</Badge>
                    )}
                  </span>
                  <span className={ui.help}>
                    {[a.website?.replace(/^https?:\/\//, ""), a.phone, a.email]
                      .filter(Boolean)
                      .join(" · ") || "Ingen kontaktinfo"}
                  </span>
                  {a.aliases.length > 0 && (
                    <span className={styles.chips}>
                      {a.aliases.map((alias) => (
                        <Badge key={alias} tone="lilac">
                          {alias}
                        </Badge>
                      ))}
                    </span>
                  )}
                </div>
                <div className={styles.itemActions}>
                  <Toggle
                    label="Vises i appen"
                    checked={a.active}
                    disabled={busyId === a.id}
                    onChange={(e) => setActive(a, e.target.checked)}
                  />
                  <Button
                    size="sm"
                    variant="secondary"
                    icon={<Icon name="edit" size={14} strokeWidth={2} />}
                    onClick={() => openForm(a)}
                  >
                    Endre
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <PageModal
        open={!!form}
        title={form?.id ? `Endre ${form.name}` : "Nytt begravelsesbyrå"}
        icon="moon"
        width={640}
        onClose={closeForm}
        footer={
          <>
            <Button variant="secondary" onClick={closeForm} disabled={isSaving}>
              Avbryt
            </Button>
            <Button onClick={save} loading={isSaving} disabled={isSaving}>
              Lagre
            </Button>
          </>
        }
      >
        {form && (
          <div className={styles.form}>
            <div className={styles.logoRow}>
              <Logo
                agency={{
                  name: form.name || "?",
                  logoUrl:
                    logoPreview ??
                    agencies?.find((a) => a.id === form.id)?.logoUrl ??
                    null,
                }}
                size={72}
              />
              <div className={styles.logoPick}>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  hidden
                  onChange={(e) => pickLogo(e.target.files?.[0] ?? null)}
                />
                <Button
                  size="sm"
                  variant="secondary"
                  icon={<Icon name="upload" size={14} strokeWidth={2} />}
                  onClick={() => fileRef.current?.click()}
                >
                  {logoFile ? "Velg en annen logo" : "Last opp logo"}
                </Button>
                <span className={ui.help}>
                  PNG eller JPG, helst kvadratisk. Lagres som PNG på høyst 512
                  px.
                </span>
              </div>
            </div>
            <TextField
              label="Navn"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Al-Jannah Begravelsesbyrå"
            />
            <TextField
              label="Nøkkel"
              value={form.key}
              onChange={(e) =>
                setForm({
                  ...form,
                  key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""),
                })
              }
              placeholder={keyFromName(form.name) || "ALJANNAH"}
              help="Brukes som kilde på dødsannonsene. Bare A–Z, 0–9 og _. Endres sjelden."
            />
            <TextField
              label="Nettside"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://www.aljannah.no"
            />
            <div className={styles.twoCols}>
              <TextField
                label="Telefon"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+47 400 34 500"
              />
              <TextField
                label="E-post"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className={styles.aliasField}>
              <TextField
                label="Aliaser"
                value={aliasDraft}
                onChange={(e) => setAliasDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === ",") {
                    e.preventDefault();
                    addAlias();
                  }
                }}
                placeholder="Andre skrivemåter, f.eks. «Alijannah» eller «aljannah.no». Enter for å legge til."
              />
              {form.aliases.length > 0 && (
                <span className={styles.chips}>
                  {form.aliases.map((alias) => (
                    <span key={alias} className={styles.chip}>
                      {alias}
                      <button
                        type="button"
                        aria-label={`Fjern ${alias}`}
                        onClick={() =>
                          setForm({
                            ...form,
                            aliases: form.aliases.filter((x) => x !== alias),
                          })
                        }
                      >
                        <Icon name="x" size={12} strokeWidth={2.2} />
                      </button>
                    </span>
                  ))}
                </span>
              )}
            </div>
            <div className={styles.twoCols}>
              <TextField
                label="Minnesider (lenke)"
                value={form.memorialListUrl}
                onChange={(e) =>
                  setForm({ ...form, memorialListUrl: e.target.value })
                }
                placeholder="https://byraa.minnesider.com"
              />
              <TextField
                label="Memcare-id"
                value={form.memcareFuneralHome}
                onChange={(e) =>
                  setForm({ ...form, memcareFuneralHome: e.target.value })
                }
                placeholder="f.eks. al-khidmat"
                help="Når satt, henter synken minnesidene herfra."
              />
            </div>
            <Toggle
              label="Vises i appen"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
          </div>
        )}
      </PageModal>
    </>
  );
};

export default FuneralAgenciesPage;
